import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

import "mapbox-gl/dist/mapbox-gl.css";
import { MapCustomControlProps } from "@app/shared/mapping/MapCustomControlProps";

import { createPortal } from "react-dom";
import { useMapboxControl } from "@app/shared/mapping/useMapBoxControl";
import {
  EXCLUDED_PROVIDER_COLOR,
  MARKET_PROVIDER_COLOR,
  MEMBERS_WITH_ACCESS_COLOR,
  MEMBERS_WITHOUT_ACCESS_COLOR,
  NETWORK_PROVIDER_COLOR,
  PROSPECT_PROVIDER_COLOR,
  TARGET_PROVIDER_COLOR,
  TERMINATED_PROVIDER_COLOR
} from "@app/projects/network-assessment/maps/network-coverage-map/NetworkCoverageMapsConstants";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { NetworkCoverageMapDataContext } from "@app/projects/network-assessment/maps/network-coverage-map/contexts/NetworkCoverageMapDataContext";
import { useContext, useMemo } from "react";
import { LegendItem } from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import { ProvidersFilterOptions } from "@app/projects/network-assessment/maps/network-coverage-map/enumerators";
import { ProviderActionStatusEnum } from "@app/projects/enumerators";
import { UserContext } from "@app/user/UserContext";
import Roles from "@app/user/roles";

const LegendSection: React.FC<{ title: string; emptyText: string; items: LegendItem[] }> = ({ title, emptyText, items }) => (
  <div>
    <span className="provider-member-layer-control__title">{title}</span>
    <div className="provider-member-layer-control__layers-container">
      {items.length > 0 ? items.map((item, index) => (
        <div key={index} className="provider-member-layer-control__item">
          <QesIcon
            className="me-2"
            height="1rem"
            iconName={item.icon}
            style={{ fill: item.color }}
            isDecorative
            width="1rem"
          />
          <span className="provider-member-layer-control__value">{item.label}</span>
        </div>
      )) :
        <span className="provider-member-layer-control__value">{emptyText}</span>
      }
    </div>
  </div>
);

const NetworkCoverageMapLegendControl: React.FC<MapCustomControlProps> = ({ ignoreInMapExport, position }) => {

  const projectContext = useContext(CountyProjectContext);
  const mapDataContext = useContext(NetworkCoverageMapDataContext);
  const userContext = useContext(UserContext);

  const showAll = useMemo(() =>
    mapDataContext.selectedProviderFilterOptions?.length === 0,
    [mapDataContext.selectedProviderFilterOptions]);

  const hasNetworkProviders = mapDataContext.providersFilterOptions?.some(
    option =>
      option.children.some(child => child.type === ProvidersFilterOptions.NonTerminated)
  );

  const hasMarketProviders = mapDataContext.providersFilterOptions?.some(
    option =>
      option.children.some(child => child.type === ProvidersFilterOptions.AvailableMarket)
  );

  const hasProspectProviders = mapDataContext.providersFilterOptions?.some(
    option =>
      option.children.some(child => child.type === ProvidersFilterOptions.AvailableProspect)
  );

  const isNetworkProvidersVisible = useMemo(() => {
    const isNetworkSelected = mapDataContext.selectedProviderFilterOptions.some(
      option => (option === ProvidersFilterOptions.Network || option === ProvidersFilterOptions.NonTerminated));

    return (isNetworkSelected || showAll) && !mapDataContext.hideNetworkProviderFilter && hasNetworkProviders;
  }, [mapDataContext.selectedProviderFilterOptions, mapDataContext.hideNetworkProviderFilter, showAll, hasNetworkProviders]);

  const isTerminatedProvidersVisible = useMemo(() => {
    const isTerminatedSelected = mapDataContext.selectedProviderFilterOptions.some(
      option => (option === ProvidersFilterOptions.Terminated));

    const hasTerminatedProviders = mapDataContext.providers && mapDataContext.providers.some(
      provider => provider.scenarioModelProviderStatus === ProviderActionStatusEnum.FullyTerminated
    );

    return (isTerminatedSelected || showAll) &&
      !mapDataContext.hideNetworkProviderFilter &&
      projectContext.runConfiguration?.isLatestPublished &&
      (userContext.roles.includes(Roles.TERM_PROVIDER_ADMIN) ||
        userContext.roles.includes(Roles.TERM_PROVIDER_VIEWER)) &&
      hasTerminatedProviders;
  }, [mapDataContext.selectedProviderFilterOptions, mapDataContext.hideNetworkProviderFilter, showAll, hasNetworkProviders]);

  const isMarketProvidersVisible = useMemo(() => {
    const isMarketSelected = mapDataContext.selectedProviderFilterOptions.some(
      option => (option === ProvidersFilterOptions.Market || option === ProvidersFilterOptions.AvailableMarket));

    return (isMarketSelected || showAll) && !mapDataContext.hideMarketProviderFilter && hasMarketProviders;
  }, [mapDataContext.selectedProviderFilterOptions, mapDataContext.hideMarketProviderFilter, showAll, hasMarketProviders]);

  const isProspectProvidersVisible = useMemo(() => {
    const isProspectSelected = mapDataContext.selectedProviderFilterOptions.some(
      option => (option === ProvidersFilterOptions.Prospect || option === ProvidersFilterOptions.AvailableProspect));

    return (isProspectSelected || showAll) && !mapDataContext.hideProspectProviderFilter && hasProspectProviders;
  }, [mapDataContext.selectedProviderFilterOptions, mapDataContext.hideProspectProviderFilter, showAll, hasProspectProviders]);

  const isTargetProvidersVisible = useMemo(() => {
    if (!mapDataContext.providersFilterOptions.some(option =>
      option.type === ProvidersFilterOptions.Target ||
      option.children?.some(child =>
        child.type === ProvidersFilterOptions.TargetedMarket ||
        child.type === ProvidersFilterOptions.TargetedProspect))
    ) {
      return false;
    }

    const isTargetSelected = mapDataContext.selectedProviderFilterOptions.some(
      option =>
      (option === ProvidersFilterOptions.Target ||
        option === ProvidersFilterOptions.TargetedMarket ||
        option === ProvidersFilterOptions.TargetedProspect));

    return (isTargetSelected || showAll) && projectContext.runConfiguration?.isLatestPublished;
  }, [mapDataContext.selectedProviderFilterOptions, mapDataContext.providersFilterOptions, projectContext.runConfiguration?.isLatestPublished, showAll]);

  const isExcludedProvidersVisible = useMemo(() => {
    if ((!mapDataContext.showExcludedProviders) ||
      !mapDataContext.providersFilterOptions.some(option =>
        option.type === ProvidersFilterOptions.Excluded ||
        option.children?.some(child =>
          child.type === ProvidersFilterOptions.ExcludedMarket ||
          child.type === ProvidersFilterOptions.ExcludedProspect))
    ) {
      return false;
    }

    const isExcludedSelected = mapDataContext.selectedProviderFilterOptions.some(
      option =>
      (option === ProvidersFilterOptions.Excluded ||
        option === ProvidersFilterOptions.ExcludedMarket ||
        option === ProvidersFilterOptions.ExcludedProspect));

    return (isExcludedSelected || showAll) && projectContext.runConfiguration?.isLatestPublished;
  }, [
    mapDataContext.selectedProviderFilterOptions,
    mapDataContext.providersFilterOptions,
    mapDataContext.showExcludedProviders,
    projectContext.runConfiguration?.isLatestPublished,
    showAll
  ]);

  const legendData = [
    {
      emptyText: "No active data layers",
      items: [
        ...mapDataContext.showMembersWithAccess ? [{
          color: MEMBERS_WITH_ACCESS_COLOR,
          icon: QesIcons.Circle,
          label: "With Access"
        }] : [],
        ...mapDataContext.showMembersWithoutAccess ? [{
          color: MEMBERS_WITHOUT_ACCESS_COLOR,
          icon: QesIcons.Circle,
          label: "Without Access"
        }] : [],
      ],
      title: "Members",
    },
    {
      emptyText: "No active data layers",
      items: [
        ...isNetworkProvidersVisible ? [{
          color: NETWORK_PROVIDER_COLOR,
          icon: QesIcons.ProviderMapPin,
          label: "Network Provider"
        }] : [],
        ...isTerminatedProvidersVisible ? [{
          color: TERMINATED_PROVIDER_COLOR,
          icon: QesIcons.ProviderMapPin,
          label: "Terminated Provider"
        }] : [],
        ...isTargetProvidersVisible ? [{
          color: TARGET_PROVIDER_COLOR,
          icon: QesIcons.ProviderMapPin,
          label: "Targeted Provider"
        }] : [],
        ...isExcludedProvidersVisible ? [{
          color: EXCLUDED_PROVIDER_COLOR,
          icon: QesIcons.ProviderMapPin,
          label: "Excluded Provider"
        }] : [],
        ...isProspectProvidersVisible ? [{
          color: PROSPECT_PROVIDER_COLOR,
          icon: QesIcons.ProviderMapPin,
          label: "Prospect Provider"
        }] : [],
        ...isMarketProvidersVisible ? [{
          color: MARKET_PROVIDER_COLOR,
          icon: QesIcons.ProviderMapPin,
          label: "Market Provider"
        }] : [],
      ],
      title: "Providers",
    },
  ] as { title: string; emptyText: string; items: LegendItem[] }[];

  const container = useMapboxControl({ mapRef: mapDataContext.mapRef, position });
  if (!container) return null;

  return createPortal(
    <div className="legend-control-container" {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>
      {legendData.map((section, index) => (
        <LegendSection key={index} emptyText={section.emptyText} title={section.title} items={section.items} />
      ))}
    </div>,
    container
  )
};

export default NetworkCoverageMapLegendControl;
